/**
 * useSessionLifecycle - Extracted from AuthContext.js
 * Manages session refresh, credit polling, sleep/wake, and activity tracking.
 * V37.0: Extracted for maintainability.
 */
import { useEffect, useRef } from 'react';
import { isPreviewEnvironment, PREVIEW_INACTIVITY_TIMEOUT } from './authHelpers';
import { clearSessionToken } from '../utils/secureStorage';
import devLog from '../utils/devLog';

export function useSessionLifecycle({
  user,
  isSessionStable,
  refreshToken,
  refreshUser,
  setUser,
  setCredits,
  setIsSessionStable,
}) {
  const refreshIntervalRef = useRef(null);
  const creditPollRef = useRef(null);
  const activityTimerRef = useRef(null);
  const previewInactivityTimerRef = useRef(null);
  const consecutiveFailures = useRef(0);
  const isPreview = isPreviewEnvironment();

  // Long-term token refresh (every 6 days)
  useEffect(() => {
    if (user && isSessionStable) {
      const refreshInterval = 6 * 24 * 60 * 60 * 1000;
      refreshIntervalRef.current = setInterval(() => {
        devLog.log('[Auth] Scheduled token refresh...');
        refreshToken();
      }, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    }
  }, [user, isSessionStable, refreshToken]);

  // Credit polling (every 30s)
  useEffect(() => {
    if (user && isSessionStable) {
      creditPollRef.current = setInterval(() => {
        refreshUser(true);
      }, 30000);

      return () => {
        if (creditPollRef.current) {
          clearInterval(creditPollRef.current);
          creditPollRef.current = null;
        }
      };
    }
  }, [user, isSessionStable, refreshUser]);

  // Sleep/Wake detection
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        devLog.log('[Auth] Tab became visible - resetting failure counter');
        consecutiveFailures.current = 0;
        if (isSessionStable) {
          refreshUser(true).catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, isSessionStable, refreshUser]);

  // Activity-based session extension
  useEffect(() => {
    if (!user || !isSessionStable) return;

    const handleActivity = () => {
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
      activityTimerRef.current = setTimeout(() => {
        devLog.log('[Auth] Activity-based session refresh...');
        refreshToken();
      }, 10 * 60 * 1000);
    };

    const events = ['click', 'keydown', 'scroll'];
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
    handleActivity();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
        activityTimerRef.current = null;
      }
    };
  }, [user, isSessionStable, refreshToken]);

  // Preview environment: 3-minute inactivity auto-logout
  useEffect(() => {
    if (!isPreview || !user) return;

    const resetInactivityTimer = () => {
      if (previewInactivityTimerRef.current) clearTimeout(previewInactivityTimerRef.current);
      previewInactivityTimerRef.current = setTimeout(() => {
        // V37.44.23 — Do NOT auto-signout while a generation stream is
        // active. During a 30-45s SSE stream the user is passively
        // watching the result render; their mouse/keyboard may not fire
        // any of the activity events for minutes. Previously this fired
        // an alert + setUser(null) mid-stream, killing the session and
        // interrupting the generation. Now we reschedule the timer so a
        // genuine idle user still gets logged out, but never during an
        // in-flight stream.
        let _streamingActive = false;
        try {
          _streamingActive = sessionStorage.getItem('ep_streaming_active') === 'true';
        } catch (_e) { /* ignore */ }
        if (_streamingActive) {
          devLog.log('[Auth] Preview inactivity: stream in-flight, deferring auto-logout.');
          resetInactivityTimer();
          return;
        }
        devLog.log('[Auth] Preview environment - 3 minutes of inactivity, auto-logout');
        clearSessionToken();
        try { sessionStorage.removeItem('ep_session_backup'); } catch (e) {}
        setUser(null);
        setCredits(null);
        setIsSessionStable(false);
        alert('You have been automatically signed out due to inactivity in the preview environment.');
      }, PREVIEW_INACTIVITY_TIMEOUT);
    };

    const activityEvents = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    activityEvents.forEach(event => window.addEventListener(event, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, resetInactivityTimer));
      if (previewInactivityTimerRef.current) {
        clearTimeout(previewInactivityTimerRef.current);
        previewInactivityTimerRef.current = null;
      }
    };
  }, [isPreview, user, setUser, setCredits, setIsSessionStable]);

  return { refreshIntervalRef, creditPollRef, activityTimerRef, previewInactivityTimerRef };
}
